/**
 * Validador de Entrada de Dados
 * 
 * Centraliza todas as validações de entrada de dados do usuário,
 * incluindo validação de formatos, tamanhos e padrões.
 * 
 * @module input.validator
 */

import type { ValidationPattern, InputValidationConfig } from '../types/security.types';

/**
 * Configuração de validação de entrada
 * 
 * Define tamanhos máximos, padrões regex e regras de validação
 * para diferentes tipos de campos de entrada.
 */
export const INPUT_VALIDATION: InputValidationConfig = {
  // Tamanhos máximos
  maxLengths: {
    username: 100,
    password: 255,
    email: 254,
    name: 100,
    description: 1000,
    filename: 255
  },
  
  // Padrões regex
  patterns: {
    username: /^[a-zA-Z0-9@._-]+$/,
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    filename: /^[a-zA-Z0-9._-]+$/,
    alphanumeric: /^[a-zA-Z0-9]+$/
  },
  
  // Caracteres proibidos
  forbiddenChars: /[\x00-\x1F\x7F<>"'&]/g,
  
  // Scripts e tags perigosas
  dangerousPatterns: [
    /<script[^>]*>.*?<\/script>/gi,
    /<[^>]*>/g,
    /javascript:/gi,
    /on\w+\s*=/gi,
    /data:text\/html/gi
  ]
};

/**
 * Valida entrada baseada em padrão
 * 
 * Valida se uma string de entrada atende aos critérios especificados:
 * - Formato (regex pattern)
 * - Tamanho máximo
 * 
 * @param input - String de entrada a ser validada
 * @param pattern - Padrão regex ou nome do padrão predefinido ('username', 'email', 'filename', 'alphanumeric')
 * @param maxLength - Tamanho máximo permitido (opcional, usa padrão do tipo se não especificado)
 * @returns true se a entrada é válida, false caso contrário
 * 
 * @example
 * ```typescript
 * validateInput('user123', 'username') // true
 * validateInput('user@name', 'username') // true
 * validateInput('user<script>', 'username') // false (contém caracteres inválidos)
 * validateInput('test@example.com', 'email') // true
 * validateInput('invalid-email', 'email') // false
 * ```
 */
export function validateInput(
  input: string, 
  pattern: ValidationPattern, 
  maxLength?: number
): boolean {
  // Entrada vazia é considerada inválida
  if (!input || typeof input !== 'string') {
    return false;
  }
  
  // Obter regex baseado no tipo de pattern
  let regex: RegExp;
  if (typeof pattern === 'string') {
    // Pattern é um nome predefinido
    if (!(pattern in INPUT_VALIDATION.patterns)) {
      console.warn(`Padrão de validação desconhecido: ${pattern}. Usando padrão alphanumeric.`);
      regex = INPUT_VALIDATION.patterns.alphanumeric;
    } else {
      regex = INPUT_VALIDATION.patterns[pattern as keyof typeof INPUT_VALIDATION.patterns];
    }
  } else {
    // Pattern é um RegExp
    regex = pattern;
  }
  
  // Obter tamanho máximo
  const maxLen = maxLength || INPUT_VALIDATION.maxLengths.username;
  
  // Validar tamanho e formato
  return input.length <= maxLen && regex.test(input);
}

/**
 * Valida se uma entrada está dentro do tamanho máximo permitido
 * 
 * @param input - String de entrada
 * @param maxLength - Tamanho máximo permitido
 * @returns true se está dentro do limite, false caso contrário
 */
export function validateInputLength(input: string, maxLength: number): boolean {
  if (!input || typeof input !== 'string') {
    return false;
  }
  return input.length <= maxLength;
}

/**
 * Valida se uma entrada corresponde a um padrão regex
 * 
 * @param input - String de entrada
 * @param pattern - Padrão regex
 * @returns true se corresponde ao padrão, false caso contrário
 */
export function validateInputPattern(input: string, pattern: RegExp): boolean {
  if (!input || typeof input !== 'string') {
    return false;
  }
  return pattern.test(input);
}

